import { Classroom, Assignment, Submission, LearningPath, ClassroomProgress, StudentAssignment, ParentChildProgress, ActivityFeedItem, CreatorTemplate, CreatorAnalytics } from '../types/classroom';

const mockClassrooms: Classroom[] = [
  {
    id: 'classroom-1',
    name: 'Computer Science 101',
    description: 'Introduction to programming concepts and computational thinking',
    teacherId: 'teacher-mrs-davis',
    studentIds: ['student-maya-14', 'student-alex-12', 'student-jordan-13'],
    classCode: 'CS101-24',
    subject: 'Computer Science',
    gradeLevel: '6th-8th Grade',
    createdAt: '2024-08-15T09:00:00Z',
    isActive: true
  },
  {
    id: 'classroom-2',
    name: 'Advanced AI Programming',
    description: 'Exploring machine learning and AI concepts through hands-on projects',
    teacherId: 'teacher-mr-thompson',
    studentIds: ['student-jordan-16', 'student-maya-14'],
    classCode: 'AI-ADV-24',
    subject: 'Advanced Computer Science',
    gradeLevel: '9th-12th Grade',
    createdAt: '2024-07-20T10:30:00Z',
    isActive: true
  },
  {
    id: 'classroom-3',
    name: 'Creative Coding Club',
    description: 'After-school club focused on creative programming and digital art',
    teacherId: 'teacher-mrs-davis',
    studentIds: ['student-maya-14', 'student-alex-12'],
    classCode: 'CREATIVE-24',
    subject: 'Arts & Technology',
    gradeLevel: 'Mixed Ages',
    createdAt: '2024-09-01T15:00:00Z',
    isActive: true
  }
];

const mockAssignments: Assignment[] = [
  {
    id: 'assignment-1',
    title: 'Build Your First AI Chatbot',
    description: 'Create a simple AI-powered chatbot that can answer questions about your favorite topic',
    classroomId: 'classroom-1',
    studentIds: ['student-maya-14', 'student-alex-12', 'student-jordan-13'],
    projectId: 'project-chatbot-template',
    dueDate: '2024-12-30T23:59:59Z',
    tasks: [
      { id: 'task-1', text: 'Complete the chatbot training tutorial', isComplete: false },
      { id: 'task-2', text: 'Choose your chatbot topic and personality', isComplete: false },
      { id: 'task-3', text: 'Train your AI model with 10+ example conversations', isComplete: false },
      { id: 'task-4', text: 'Test your chatbot with different questions', isComplete: false },
      { id: 'task-5', text: 'Add visual elements and styling', isComplete: false }
    ],
    createdAt: '2024-12-10T09:00:00Z',
    updatedAt: '2024-12-15T14:30:00Z',
    status: 'published',
    instructions: 'This project will help you understand the basics of AI conversation models and natural language processing.',
    estimatedTime: 120,
    difficulty: 'beginner'
  },
  {
    id: 'assignment-2',
    title: 'Smart Image Classifier',
    description: 'Train an AI model to classify images and use it in an interactive application',
    classroomId: 'classroom-2',
    studentIds: ['student-jordan-16', 'student-maya-14'],
    projectId: 'project-image-classifier',
    dueDate: '2025-01-15T23:59:59Z',
    tasks: [
      { id: 'task-6', text: 'Collect and organize training images (minimum 50 per class)', isComplete: false },
      { id: 'task-7', text: 'Train your image classification model', isComplete: false },
      { id: 'task-8', text: 'Achieve at least 85% accuracy on test data', isComplete: false },
      { id: 'task-9', text: 'Build an interactive app using your trained model', isComplete: false },
      { id: 'task-10', text: 'Write a reflection on AI bias and ethical considerations', isComplete: false }
    ],
    createdAt: '2024-12-05T11:00:00Z',
    updatedAt: '2024-12-15T16:45:00Z',
    status: 'published',
    instructions: 'This advanced project explores computer vision and machine learning. Pay special attention to data quality and model evaluation.',
    estimatedTime: 180,
    difficulty: 'advanced'
  },
  {
    id: 'assignment-3',
    title: 'Interactive Story with AI Characters',
    description: 'Create an interactive story where AI characters respond dynamically to user choices',
    classroomId: 'classroom-3',
    studentIds: ['student-maya-14', 'student-alex-12'],
    dueDate: '2024-12-25T23:59:59Z',
    tasks: [
      { id: 'task-11', text: 'Plan your story structure and characters', isComplete: false },
      { id: 'task-12', text: 'Create character personalities using AI prompts', isComplete: false },
      { id: 'task-13', text: 'Build the interactive story interface', isComplete: false },
      { id: 'task-14', text: 'Add voice and visual elements', isComplete: false }
    ],
    createdAt: '2024-12-08T13:00:00Z',
    updatedAt: '2024-12-15T10:15:00Z',
    status: 'published',
    instructions: 'Focus on creativity and storytelling while exploring how AI can enhance interactive narratives.',
    estimatedTime: 90,
    difficulty: 'intermediate'
  }
];

const mockSubmissions: Submission[] = [
  // Assignment 1 submissions
  {
    id: 'submission-1',
    assignmentId: 'assignment-1',
    studentId: 'student-maya-14',
    projectId: 'project-maya-chatbot',
    status: 'in_progress',
    completedTasks: ['task-1', 'task-2']
  },
  {
    id: 'submission-2',
    assignmentId: 'assignment-1',
    studentId: 'student-alex-12',
    projectId: 'project-alex-chatbot',
    status: 'not_started',
    completedTasks: []
  },
  {
    id: 'submission-3',
    assignmentId: 'assignment-1',
    studentId: 'student-jordan-13',
    projectId: 'project-jordan-chatbot',
    status: 'submitted',
    submittedAt: '2024-12-14T16:30:00Z',
    completedTasks: ['task-1', 'task-2', 'task-3', 'task-4', 'task-5']
  },
  // Assignment 2 submissions
  {
    id: 'submission-4',
    assignmentId: 'assignment-2',
    studentId: 'student-jordan-16',
    status: 'in_progress',
    completedTasks: ['task-6', 'task-7']
  },
  {
    id: 'submission-5',
    assignmentId: 'assignment-2',
    studentId: 'student-maya-14',
    status: 'in_progress',
    completedTasks: ['task-6']
  }
];

const mockLearningPaths: LearningPath[] = [
  {
    id: 'path-1',
    title: 'Introduction to AI and Machine Learning',
    description: 'A comprehensive learning path covering AI fundamentals and practical applications',
    teacherId: 'teacher-mrs-davis',
    modules: [
      {
        id: 'module-1',
        title: 'What is Artificial Intelligence?',
        description: 'Understanding AI basics and real-world applications',
        content: 'Introduction to AI concepts, history, and current applications...',
        duration: 30,
        order: 1,
        type: 'lesson'
      },
      {
        id: 'module-2',
        title: 'Training Your First AI Model',
        description: 'Hands-on experience with AI model training',
        content: 'Step-by-step guide to training a simple AI model...',
        duration: 45,
        order: 2,
        type: 'exercise'
      }
    ],
    estimatedDuration: 4,
    difficulty: 'beginner',
    tags: ['AI', 'machine-learning', 'introduction'],
    createdAt: '2024-11-01T10:00:00Z',
    isPublished: true
  }
];

// Mock data for student assignments
const mockStudentAssignments: StudentAssignment[] = [
  {
    assignment: mockAssignments[0],
    submission: mockSubmissions[0],
    classroom: mockClassrooms[0],
    isOverdue: false,
    daysUntilDue: 15
  },
  {
    assignment: mockAssignments[2],
    submission: mockSubmissions.find(s => s.assignmentId === 'assignment-3'),
    classroom: mockClassrooms[2],
    isOverdue: false,
    daysUntilDue: 10
  }
];

// Mock data for parent dashboard
const mockParentChildProgress: ParentChildProgress[] = [
  {
    childId: 'child-emma-8',
    childName: 'Emma Johnson',
    assignments: mockStudentAssignments,
    recentActivity: [
      {
        id: 'activity-1',
        type: 'assignment_started',
        title: 'Started AI Chatbot Project',
        description: 'Emma began working on her first AI chatbot assignment',
        timestamp: '2024-12-15T10:30:00Z',
        studentId: 'child-emma-8',
        studentName: 'Emma Johnson',
        relatedId: 'assignment-1'
      },
      {
        id: 'activity-2',
        type: 'project_created',
        title: 'Created Personal Art Project',
        description: 'Emma started a new creative coding project',
        timestamp: '2024-12-14T14:20:00Z',
        studentId: 'child-emma-8',
        studentName: 'Emma Johnson',
        relatedId: 'project-emma-art'
      }
    ],
    overallProgress: {
      completed: 2,
      inProgress: 3,
      notStarted: 1
    }
  }
];

// Mock data for creator templates
const mockCreatorTemplates: CreatorTemplate[] = [
  {
    id: 'template-1',
    title: 'Interactive Story Builder',
    description: 'A template for creating branching narrative stories with AI characters',
    status: 'approved',
    submittedAt: '2024-11-15T09:00:00Z',
    reviewedAt: '2024-11-20T15:30:00Z',
    usageCount: 245,
    rating: 4.7,
    tags: ['storytelling', 'AI', 'creativity'],
    difficulty: 'intermediate',
    creatorId: 'creator-alex-kim'
  },
  {
    id: 'template-2',
    title: 'AI Pet Trainer',
    description: 'Train a virtual pet using voice commands and machine learning',
    status: 'in_review',
    submittedAt: '2024-12-10T11:20:00Z',
    usageCount: 0,
    rating: 0,
    tags: ['AI', 'pets', 'voice-recognition'],
    difficulty: 'beginner',
    creatorId: 'creator-alex-kim'
  }
];

const mockCreatorAnalytics: CreatorAnalytics = {
  totalTemplates: 5,
  approvedTemplates: 3,
  totalUsage: 1250,
  averageRating: 4.5,
  recentUsage: [
    {
      templateId: 'template-1',
      templateTitle: 'Interactive Story Builder',
      usageCount: 45,
      period: 'Last 30 days'
    },
    {
      templateId: 'template-3',
      templateTitle: 'Math Game Creator',
      usageCount: 32,
      period: 'Last 30 days'
    }
  ]
};

// Mock API functions
export const getTeacherClassrooms = (teacherId: string): Promise<Classroom[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const classrooms = mockClassrooms.filter(c => c.teacherId === teacherId);
      resolve(classrooms);
    }, 500);
  });
};

export const getClassroomStudents = (classroomId: string): Promise<any[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const classroom = mockClassrooms.find(c => c.id === classroomId);
      if (classroom) {
        // In a real app, this would fetch actual student data
        const mockStudents = classroom.studentIds.map(id => ({
          id,
          name: `Student ${id.split('-')[1]}`,
          email: `${id}@school.edu`,
          status: 'active'
        }));
        resolve(mockStudents);
      } else {
        resolve([]);
      }
    }, 300);
  });
};

export const getTeacherAssignments = (teacherId: string): Promise<Assignment[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const teacherClassrooms = mockClassrooms.filter(c => c.teacherId === teacherId);
      const classroomIds = teacherClassrooms.map(c => c.id);
      const assignments = mockAssignments.filter(a => classroomIds.includes(a.classroomId));
      resolve(assignments);
    }, 400);
  });
};

export const getAssignmentProgress = (assignmentId: string): Promise<ClassroomProgress> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const assignment = mockAssignments.find(a => a.id === assignmentId);
      const submissions = mockSubmissions.filter(s => s.assignmentId === assignmentId);
      
      if (assignment) {
        const totalStudents = assignment.studentIds.length;
        const notStarted = submissions.filter(s => s.status === 'not_started').length;
        const inProgress = submissions.filter(s => s.status === 'in_progress').length;
        const submitted = submissions.filter(s => s.status === 'submitted').length;
        const reviewed = submissions.filter(s => s.status === 'reviewed').length;
        
        const progress: ClassroomProgress = {
          classroomId: assignment.classroomId,
          assignmentId,
          totalStudents,
          notStarted,
          inProgress,
          submitted,
          reviewed,
          averageProgress: ((inProgress + submitted + reviewed) / totalStudents) * 100,
          submissions
        };
        
        resolve(progress);
      }
    }, 300);
  });
};

export const getStudentAssignments = (studentId: string): Promise<StudentAssignment[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const assignments = mockAssignments.filter(a => a.studentIds.includes(studentId));
      const studentAssignments = assignments.map(assignment => {
        const submission = mockSubmissions.find(s => s.assignmentId === assignment.id && s.studentId === studentId);
        const classroom = mockClassrooms.find(c => c.id === assignment.classroomId)!;
        const dueDate = new Date(assignment.dueDate);
        const now = new Date();
        const daysUntilDue = Math.ceil((dueDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
        
        return {
          assignment,
          submission,
          classroom,
          isOverdue: daysUntilDue < 0,
          daysUntilDue
        };
      });
      
      resolve(studentAssignments.sort((a, b) => a.daysUntilDue - b.daysUntilDue));
    }, 400);
  });
};

export const getParentChildProgress = (parentId: string): Promise<ParentChildProgress[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      // In a real app, this would fetch actual child data based on parent ID
      resolve(mockParentChildProgress);
    }, 500);
  });
};

export const getCreatorTemplates = (creatorId: string): Promise<CreatorTemplate[]> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const templates = mockCreatorTemplates.filter(t => t.creatorId === creatorId);
      resolve(templates);
    }, 400);
  });
};

export const getCreatorAnalytics = (creatorId: string): Promise<CreatorAnalytics> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(mockCreatorAnalytics);
    }, 300);
  });
};

export const createClassroom = (classroomData: Omit<Classroom, 'id' | 'createdAt' | 'classCode'>): Promise<Classroom> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const newClassroom: Classroom = {
        ...classroomData,
        id: `classroom-${Date.now()}`,
        classCode: `CLS-${Math.random().toString(36).substring(2, 8).toUpperCase()}`,
        createdAt: new Date().toISOString()
      };
      mockClassrooms.push(newClassroom);
      resolve(newClassroom);
    }, 500);
  });
};

export const createAssignment = (assignmentData: Omit<Assignment, 'id' | 'createdAt' | 'updatedAt'>): Promise<Assignment> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const newAssignment: Assignment = {
        ...assignmentData,
        id: `assignment-${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      mockAssignments.push(newAssignment);
      resolve(newAssignment);
    }, 600);
  });
};

export const submitAssignment = (assignmentId: string, studentId: string, projectId: string): Promise<Submission> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      const newSubmission: Submission = {
        id: `submission-${Date.now()}`,
        assignmentId,
        studentId,
        projectId,
        status: 'submitted',
        submittedAt: new Date().toISOString(),
        completedTasks: []
      };
      mockSubmissions.push(newSubmission);
      resolve(newSubmission);
    }, 300);
  });
};